import React, { useState } from 'react';
import { supabase } from '../services/supabaseClient';

interface PendingTransferProps {
  org: any;
  onTransferCancelled?: () => void;
}

export const PendingTransfer: React.FC<PendingTransferProps> = ({ org, onTransferCancelled }) => {
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if transfer is expired
  const isExpired = org.transfer_expires_at && new Date(org.transfer_expires_at) < new Date();
  const expiresIn = org.transfer_expires_at ? new Date(org.transfer_expires_at) : null;

  const handleCancelTransfer = async () => {
    setCancelling(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch('/api/orgs/transfer/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ orgId: org.id })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to cancel transfer');
      }

      if (onTransferCancelled) {
        onTransferCancelled();
      }

    } catch (err: any) {
      console.error('Cancel transfer error:', err);
      setError(err.message || 'Failed to cancel transfer');
    } finally {
      setCancelling(false);
    }
  };

  const handleClearExpired = async () => {
    // For expired transfers, just clear the fields locally
    setCancelling(true);
    
    try {
      const { error: updateError } = await supabase
        .from('sf_orgs')
        .update({
          transfer_token: null,
          transfer_expires_at: null,
          transfer_note: null
        })
        .eq('id', org.id);

      if (updateError) throw updateError;

      if (onTransferCancelled) {
        onTransferCancelled();
      }
    } catch (err: any) {
      console.error('Clear expired transfer error:', err);
      setError('Failed to clear expired transfer');
    } finally {
      setCancelling(false);
    }
  };

  if (!org.transfer_token) return null;

  return (
    <div className={`rounded-md p-4 mb-4 ${isExpired ? 'bg-gray-50 border border-gray-200' : 'bg-yellow-50 border border-yellow-200'}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          {isExpired ? (
            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${isExpired ? 'text-gray-800' : 'text-yellow-800'}`}>
            {isExpired ? 'Transfer Expired' : 'Transfer Pending'}
          </h3>
          <div className={`mt-2 text-sm ${isExpired ? 'text-gray-700' : 'text-yellow-700'}`}>
            {isExpired ? (
              <p>The transfer invitation has expired. Clear this to enable new transfers.</p>
            ) : (
              <>
                <p>This organization has a pending transfer.</p>
                {expiresIn && (
                  <p className="mt-1">
                    Expires: {expiresIn.toLocaleString()}
                  </p>
                )}
                {org.transfer_note && (
                  <p className="mt-1 italic">
                    Note: {org.transfer_note}
                  </p>
                )}
              </>
            )}
          </div>
          {error && (
            <p className="mt-2 text-sm text-red-600">{error}</p>
          )}
          <div className="mt-3">
            <button
              onClick={isExpired ? handleClearExpired : handleCancelTransfer}
              disabled={cancelling}
              className={`text-sm font-medium ${
                isExpired 
                  ? 'text-gray-600 hover:text-gray-500' 
                  : 'text-yellow-600 hover:text-yellow-500'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {cancelling ? 'Processing...' : isExpired ? 'Clear Expired Transfer' : 'Cancel Transfer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};